import React, { useState, useEffect } from "react";
import { Plus, Search, FileText, Edit2, Trash2, AlertCircle, Loader2, Info } from "lucide-react";
import PacienteForm from "../components/forms/pacienteForm";
import { usePacientes } from "../hooks/usePacientes";
import { usePaginationWithFilter } from "../hooks/usePagination";
import Pagination from "../components/ui/Pagination";
import FacturaForm from "./Facturacion/FacturaForm";
import Modal from "../components/Modal";
import { deletePaciente } from "../services/pacientesService";
import { toast } from "react-hot-toast";
import { Paciente } from "../types";
import { useAuth } from "../context/AuthContext";

const Pacientes = () => {
  // Estados
  const { pacientes, loading, error, loadPacientes } = usePacientes();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPaciente, setSelectedPaciente] = useState<Paciente | null>(null);
  const [showFacturaForm, setShowFacturaForm] = useState(false);
  const [showPacienteForm, setShowPacienteForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deletingPaciente, setDeletingPaciente] = useState<Paciente | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPacienteInfo, setShowPacienteInfo] = useState(false);

  // Verificar si el usuario puede eliminar pacientes
  const canDeletePatients = user && (user.role === 'admin' || user.role === 'programer');

  // Realizar búsqueda en el backend cuando el usuario escribe
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm.length >= 4) {
        // Buscar en el backend sin límite
        loadPacientes({ search: searchTerm });
      } else if (searchTerm.length === 0) {
        // Cargar todos los pacientes cuando se borra la búsqueda
        loadPacientes();
      }
    }, 500); // Esperar 500ms después de que el usuario deje de escribir

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // Función de filtrado para la paginación
  const filterPacientes = (paciente: Paciente, term: string) => {
    return Object.values(paciente)
      .join(" ")
      .toLowerCase()
      .includes(term);
  };

  // Hook de paginación - sin filtrado local ya que filtramos en el backend
  const {
    currentItems: paginatedPacientes,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
    isFiltered
  } = usePaginationWithFilter(
    pacientes,
    filterPacientes,
    '', // No filtrar localmente, ya filtramos en el backend
    25 // 25 elementos por página por defecto
  );

  // Handlers
  const handleViewInfo = (paciente: Paciente) => {
    setSelectedPaciente(paciente);
    setShowPacienteInfo(true);
  };

  const handleFacturar = (paciente: Paciente) => {
    setSelectedPaciente(paciente);
    setShowFacturaForm(true);
  };

  const handleEdit = (paciente: Paciente) => {
    setSelectedPaciente(paciente);
    setIsEditing(true);
    setShowPacienteForm(true);
  };

  const handleCloseForm = () => {
    setShowPacienteForm(false);
    setSelectedPaciente(null);
    setIsEditing(false);
  };

  const handleNewPaciente = () => {
    setIsEditing(false);
    setSelectedPaciente(null);
    setShowPacienteForm(true);
  };

  const handleSuccess = async () => {
    if (isEditing) {
      // The hook already updated the list in place, so no refetch: refetching
      // here would drop the active search term and the 100-record backend cap
      // could make older patients vanish from the list.
      toast.success('Paciente actualizado con éxito');
    } else {
      await loadPacientes();
      toast.success('Paciente creado con éxito');
    }
    handleCloseForm();
  };

  const handleDeleteClick = (paciente: Paciente) => {
    if (!canDeletePatients) {
      toast.error('No tienes permisos para eliminar pacientes');
      return;
    }
    setDeletingPaciente(paciente);
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingPaciente) return;
    if (!deletingPaciente.codigo) {
      toast.error('Error: El paciente no tiene un código asignado');
      setIsDeleting(false);
      setShowConfirmDelete(false);
      setDeletingPaciente(null);
      return;
    }

    setIsDeleting(true);
    try {
      await deletePaciente(deletingPaciente.codigo);
      await loadPacientes();
      toast.success('Paciente eliminado con éxito');
    } catch (error) {
      toast.error('Error al eliminar el paciente');

    } finally {
      setIsDeleting(false);
      setShowConfirmDelete(false);
      setDeletingPaciente(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Pacientes</h1>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors duration-200"
          onClick={handleNewPaciente}
        >
          <Plus className="h-5 w-5" />
          <span>Nuevo Paciente</span>
        </button>
      </div>

      {/* Búsqueda y Filtros */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Buscar por Nombre, Código, Documento..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
            {searchTerm && searchTerm.length < 4 && (
              <div className="absolute top-full left-0 mt-1 text-xs text-gray-600">
                Ingrese al menos 4 caracteres para buscar ({searchTerm.length}/4)
              </div>
            )}
          </div>
          {/* Aquí podrías agregar más filtros si es necesario */}
        </div>
      </div>

      {/* Lista de Pacientes */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center p-8 text-red-600">
            <AlertCircle className="h-6 w-6 mr-2" />
            <span>{error}</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Código</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Nombre</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Procedencia</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Teléfono</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedPacientes.map((paciente: Paciente) => (
                  <tr
                    key={paciente._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="py-3 px-4">{paciente.codigo}</td>
                    <td className="py-3 px-4 font-medium">{paciente.nombre}</td>
                    <td className="py-3 px-4">{paciente.procedencia}</td>
                    <td className="py-3 px-4">{paciente.telefono}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleViewInfo(paciente)}
                          className="text-indigo-600 hover:text-indigo-800 transition-colors duration-150"
                          title="Ver información del paciente"
                        >
                          <Info className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(paciente)}
                          className="text-blue-600 hover:text-blue-800 transition-colors duration-150"
                          title="Editar paciente"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        {canDeletePatients && (
                          <button
                            onClick={() => handleDeleteClick(paciente)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-150"
                            title="Eliminar paciente"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleFacturar(paciente)}
                          className="text-green-600 hover:text-green-800 transition-colors duration-150"
                          title="Crear factura"
                        >
                          <FileText className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {paginatedPacientes.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-500">
                      {isFiltered ?
                        "No se encontraron pacientes que coincidan con la búsqueda." :
                        "No hay pacientes registrados."
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow p-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            showItemsPerPage={true}
          />
        </div>
      )}

      {/* Modales */}
      <Modal
        isOpen={showPacienteForm}
        onClose={handleCloseForm}
        maxWidth="max-w-2xl"
      >
        <PacienteForm
          onClose={handleCloseForm}
          onSuccess={handleSuccess}
          pacienteToEdit={selectedPaciente as any}
          isEditing={isEditing}
        />
      </Modal>

      <Modal
        isOpen={showFacturaForm}
        onClose={() => setShowFacturaForm(false)}
        maxWidth="max-w-4xl"
      >
        <FacturaForm
          onClose={() => {
            setShowFacturaForm(false);
            setSelectedPaciente(null);
          }}
          paciente={selectedPaciente as any}
        />
      </Modal>

      {/* Modal para visualizar información del paciente */}
      <Modal
        isOpen={showPacienteInfo}
        onClose={() => setShowPacienteInfo(false)}
        maxWidth="max-w-2xl"
      >
        {selectedPaciente && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">
              Información del Paciente
            </h2>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-lg mb-4 text-blue-700">Datos Personales</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Código</p>
                    <p className="font-medium">{selectedPaciente.codigo || 'No asignado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Nombre</p>
                    <p className="font-medium">{selectedPaciente.nombre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Documento</p>
                    <p className="font-medium">{selectedPaciente.documento}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Género</p>
                    <p className="font-medium">{selectedPaciente.genero || 'No especificado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fecha de Nacimiento</p>
                    <p className="font-medium">
                      {selectedPaciente.fechaNacimiento
                        ? new Date(selectedPaciente.fechaNacimiento).toLocaleDateString()
                        : 'No especificada'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Nacionalidad</p>
                    <p className="font-medium">{selectedPaciente.nacionalidad || 'No especificada'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-4 text-blue-700">Información de Contacto</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Teléfono</p>
                    <p className="font-medium">{selectedPaciente.telefono || 'No especificado'}</p>
                  </div>
                  <div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Dirección</p>
                    <p className="font-medium">{selectedPaciente.direccion || 'No especificada'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Procedencia</p>
                    <p className="font-medium">{selectedPaciente.procedencia || 'No especificada'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Seguro</p>
                    <p className="font-medium">
                      {typeof selectedPaciente.seguro === 'object'
                        ? selectedPaciente.seguro?.nombre
                        : selectedPaciente.seguro || 'No especificado'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">No. Afiliado</p>
                    <p className="font-medium">{selectedPaciente.noAfiliado || 'No especificado'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setShowPacienteInfo(false)}
                className="px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        maxWidth="max-w-md"
      >
        <div className="p-6">
          <div className="flex items-center justify-center mb-4 text-red-600">
            <AlertCircle className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-center mb-4">
            ¿Está seguro que desea eliminar al paciente?
          </h3>
          <p className="text-gray-500 text-center mb-6">
            {deletingPaciente?.nombre}
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setShowConfirmDelete(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={isDeleting}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Eliminando...</span>
                </>
              ) : (
                <span>Eliminar</span>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Pacientes;
